// src/components/CarrierRegistrationForm.tsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faUser,
  faEnvelope,
  faPhone,
  faIdCard,
  faTruck,
  faPaperPlane,
  faEdit,
  faShieldAlt,
  faDollarSign,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Icon } from '@iconify/react';
import { useIncompleteRegistration } from '../hooks/useIncompleteRegistration';
import { useCarrier, CarrierFormData } from '../hooks/useCarrier';
import IncompleteRegistrationDialog from './IncompleteRegistrationDialog';
import FloatingAlert from './ui/FloatingAlert';
import useAlert from '../hooks/useAlert';

interface CarrierRegistrationFormProps {
  onClose: () => void;
  onSuccess: (recordId?: string) => void;
}

const equipmentTypes = ['Open Carrier', 'Enclosed Carrier', 'Flatbed', 'Other'];

// Sanitización básica para evitar XSS al mostrar datos en el frontend
function escape(str: string) {
  return str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const CarrierRegistrationForm: React.FC<CarrierRegistrationFormProps> = ({
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CarrierFormData>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    dotNumber: '',
    equipmentType: '',
    mcNumber: '',
    insurancePolicyNumber: '',
    insuranceProvider: '',
    insuranceCoverageAmount: '',
    insuranceExpirationDate: '',
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [recordId, setRecordId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Use custom hook for carrier logic
  const { loading, errors, submitCarrier, validateField, clearFieldError, sanitizeDOT } = useCarrier();
  const { alert, showError, showSuccess, hideAlert } = useAlert();

  const {
    loading: checkingIncomplete,
    showDialog,
    continueWithPrevious,
    discardAndCreateNew,
  } = useIncompleteRegistration('carrier');

  // Auto-hide alert after 5 seconds
  React.useEffect(() => {
    if (alert?.isVisible) {
      const timeout = setTimeout(() => {
        hideAlert();
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [alert, hideAlert]);

  // Manejar la decisión del diálogo
  const handleContinueWithPrevious = () => {
    const record = continueWithPrevious();
    if (record) {
      setIsUpdating(true);
      setRecordId(record.id);
      setFormData({
        companyName: record.data.company_name || '',
        contactName: record.data.contact_name || '',
        email: record.data.email || '',
        phone: record.data.phone || '',
        dotNumber: record.data.dot_number || '',
        equipmentType: record.data.equipment_type || '',
        mcNumber: record.data.mc_number || '',
        insurancePolicyNumber: record.data.insurance_policy_number || '',
        insuranceProvider: record.data.insurance_provider || '',
        insuranceCoverageAmount: record.data.insurance_coverage_amount 
          ? (record.data.insurance_coverage_amount / 100).toFixed(2) 
          : '',
        insuranceExpirationDate: record.data.insurance_expiration_date || '',
      });
    }
  };

  const handleDiscardAndCreateNew = async () => {
    await discardAndCreateNew();
    setIsUpdating(false);
    setRecordId(null);
    setFormData({
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      dotNumber: '',
      equipmentType: '',
      mcNumber: '',
      insurancePolicyNumber: '',
      insuranceProvider: '',
      insuranceCoverageAmount: '',
      insuranceExpirationDate: '',
    });
  };

  const handleChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Sanitización especial para DOT
    const newValue = name === 'dotNumber' ? sanitizeDOT(value) : value;

    setFormData((prev) => ({ ...prev, [name]: newValue }));
    clearFieldError(name);

  };

  const handleBlur = async (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    await validateField(name, value);

  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    hideAlert(); // Clear any existing alerts
    setSubmitted(false); // Reset submitted state

    const result = await submitCarrier(formData, isUpdating, recordId);
    
    if (result.success) {
      setSubmitted(true); // Mark as submitted to disable button
      // Show success alert first
      showSuccess(
        'Registration submitted successfully! Thank you for registering as a carrier. Registration is completely free.',
        'Registration Successful'
      );
      // Close modal after showing alert (delay to let user see the message)
      setTimeout(() => {
      onSuccess(result.recordId || undefined);
      // The success message will be shown by App.tsx after modal closes
      }, 2000);
    } else if (result.error) {
      showError(
        result.error.includes('already exists')
          ? 'A carrier with this email or DOT number is already registered. Please use different credentials or contact support if this is an error.'
          : result.error,
        'Registration Failed'
      );
    }
  };

  return (
    <div className="bg-white pt-14 px-4 sm:px-6 pb-6 rounded-xl sm:rounded-2xl shadow-lg max-w-xl w-[92vw] sm:w-full max-h-[calc(100vh-120px)] overflow-y-auto relative">
      {/* Floating alert for success/error messages */}
      <FloatingAlert
        type={alert?.type || 'info'}
        message={alert?.message || ''}
        title={alert?.title}
        isVisible={alert?.isVisible || false}
        onDismiss={hideAlert}
      />
      <button
        onClick={onClose}
        aria-label="Close modal"
        className="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full text-[#003380] hover:text-[#002660] hover:bg-gray-100 transition-colors"
      >
        <Icon icon="mdi:close" className="w-5 h-5" />
      </button>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" noValidate>
        {/* Company Name */}
        <div className="flex flex-col gap-2">
          <label htmlFor="carrier-company" className="text-left text-sm font-medium text-gray-700">Company Name *</label>
          <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
              <FontAwesomeIcon icon={faBuilding} className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <input
              id="carrier-company"
              type="text"
              name="companyName"
              placeholder="e.g., ABC Transport LLC"
              onChange={handleChange}
              onBlur={handleBlur}
              value={formData.companyName}
              className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
            />
          </div>
          {errors.companyName && (
            <span className="text-red-600 text-sm">
              {escape(errors.companyName)}
            </span>
          )}
        </div>

        {/* Contact Name */}
        <div className="flex flex-col gap-2">
          <label htmlFor="carrier-contact" className="text-left text-sm font-medium text-gray-700">Contact Name *</label>
          <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
              <FontAwesomeIcon icon={faUser} className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <input
              id="carrier-contact"
              type="text"
              name="contactName"
              placeholder="e.g., John Smith"
              onChange={handleChange}
              onBlur={handleBlur}
              value={formData.contactName}
              className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
            />
          </div>
          {errors.contactName && (
            <span className="text-red-600 text-sm">
              {escape(errors.contactName)}
            </span>
          )}
        </div>

        {/* Email */}
        <div className="flex flex-col gap-2">
          <label htmlFor="carrier-email" className="text-left text-sm font-medium text-gray-700">Email *</label>
          <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
              <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <input
              id="carrier-email"
              type="email"
              name="email"
              placeholder="e.g., contact@company.com"
              onChange={handleChange}
              onBlur={handleBlur}
              value={formData.email}
              className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
            />
          </div>
          {errors.email && (
            <span className="text-red-600 text-sm">{escape(errors.email)}</span>
          )}
        </div>

        {/* Phone */}
        <div className="flex flex-col gap-2">
          <label htmlFor="carrier-phone" className="text-left text-sm font-medium text-gray-700">Phone *</label>
          <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
              <FontAwesomeIcon icon={faPhone} className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <input
              id="carrier-phone"
              type="tel"
              name="phone"
              placeholder="e.g., (800) 555-1234"
              onChange={handleChange}
              onBlur={handleBlur}
              value={formData.phone}
              className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
            />
          </div>
          {errors.phone && (
            <span className="text-red-600 text-sm">{escape(errors.phone)}</span>
          )}
        </div>

        {/* USDOT Number */}
        <div className="flex flex-col gap-2">
          <label htmlFor="carrier-dot" className="text-left text-sm font-medium text-gray-700">USDOT Number *</label>
          <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
              <FontAwesomeIcon icon={faIdCard} className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <input
              id="carrier-dot"
              type="text"
              name="dotNumber"
              placeholder="e.g., 123456"
              onChange={handleChange}
              onBlur={handleBlur}
              value={formData.dotNumber}
              className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
            />
          </div>
          {errors.dotNumber && (
            <span className="text-red-600 text-sm">
              {escape(errors.dotNumber)}
            </span>
          )}
        </div>

        {/* Equipment Type */}
        <div className="flex flex-col gap-2">
          <label htmlFor="carrier-equipment" className="text-left text-sm font-medium text-gray-700">Equipment Type *</label>
          <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
              <FontAwesomeIcon icon={faTruck} className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <select
              id="carrier-equipment"
              name="equipmentType"
              value={formData.equipmentType}
              onChange={handleChange}
              onBlur={handleBlur}
              className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none bg-white text-gray-900"
            >
              <option value="">Choose equipment type</option>
              {equipmentTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          {errors.equipmentType && (
            <span className="text-red-600 text-sm">
              {escape(errors.equipmentType)}
            </span>
          )}
        </div>

        {/* MC Number (Optional) */}
        <div className="flex flex-col gap-2">
          <label htmlFor="carrier-mc" className="text-left text-sm font-medium text-gray-700">MC Number <span className="text-gray-500 font-normal">(optional)</span></label>
          <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
              <FontAwesomeIcon icon={faIdCard} className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <input
              id="carrier-mc"
              type="text"
              name="mcNumber"
              placeholder="e.g., 123456"
              onChange={handleChange}
              onBlur={handleBlur}
              value={formData.mcNumber}
              className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
            />
          </div>
          {errors.mcNumber && (
            <span className="text-red-600 text-sm">
              {escape(errors.mcNumber)}
            </span>
          )}
        </div>

        {/* Insurance Information Section */}
        <div className="border-t border-gray-200 pt-5 mt-2">
          <h3 className="text-left text-sm font-semibold text-gray-700 mb-4">Insurance Information <span className="font-normal text-gray-500">(optional)</span></h3>
          
          {/* Insurance Policy Number */}
          <div className="flex flex-col gap-2 mb-4">
            <label htmlFor="carrier-insurance-policy" className="text-left text-sm font-medium text-gray-700">Policy Number</label>
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
                <FontAwesomeIcon icon={faShieldAlt} className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <input
                id="carrier-insurance-policy"
                type="text"
                name="insurancePolicyNumber"
                placeholder="Insurance policy number"
                onChange={handleChange}
                onBlur={handleBlur}
                value={formData.insurancePolicyNumber}
                className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
              />
            </div>
            {errors.insurancePolicyNumber && (
              <span className="text-red-600 text-sm">
                {escape(errors.insurancePolicyNumber)}
              </span>
            )}
          </div>

          {/* Insurance Provider */}
          <div className="flex flex-col gap-2 mb-4">
            <label htmlFor="carrier-insurance-provider" className="text-left text-sm font-medium text-gray-700">Provider / Company</label>
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
                <FontAwesomeIcon icon={faBuilding} className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <input
                id="carrier-insurance-provider"
                type="text"
                name="insuranceProvider"
                placeholder="Insurance provider name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={formData.insuranceProvider}
                className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
              />
            </div>
            {errors.insuranceProvider && (
              <span className="text-red-600 text-sm">
                {escape(errors.insuranceProvider)}
              </span>
            )}
          </div>

          {/* Insurance Coverage Amount */}
          <div className="flex flex-col gap-2 mb-4">
            <label htmlFor="carrier-insurance-amount" className="text-left text-sm font-medium text-gray-700">Coverage Amount (USD)</label>
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
                <FontAwesomeIcon icon={faDollarSign} className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <input
                id="carrier-insurance-amount"
                type="number"
                name="insuranceCoverageAmount"
                placeholder="e.g., 1000000"
                onChange={handleChange}
                onBlur={handleBlur}
                value={formData.insuranceCoverageAmount}
                className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
                min="0"
                step="1000"
              />
            </div>
            {errors.insuranceCoverageAmount && (
              <span className="text-red-600 text-sm">
                {escape(errors.insuranceCoverageAmount)}
              </span>
            )}
          </div>

          {/* Insurance Expiration Date */}
          <div className="flex flex-col gap-2 mb-4">
            <label htmlFor="carrier-insurance-exp" className="text-left text-sm font-medium text-gray-700">Expiration Date</label>
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
                <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <input
                id="carrier-insurance-exp"
                type="date"
                name="insuranceExpirationDate"
                onChange={handleChange}
                onBlur={handleBlur}
                value={formData.insuranceExpirationDate}
                className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            {errors.insuranceExpirationDate && (
              <span className="text-red-600 text-sm">
                {escape(errors.insuranceExpirationDate)}
              </span>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full flex items-center justify-center min-h-[48px] sm:min-h-[52px] bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold transition-all hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading || checkingIncomplete || submitted}
        >
          <FontAwesomeIcon
            icon={isUpdating ? faEdit : faPaperPlane}
            className="mr-2"
          />
          {loading
            ? isUpdating
              ? 'Updating...'
              : 'Sending...'
            : isUpdating
              ? 'Update Registration'
              : 'Submit Registration'}
        </button>

        {/* Mensaje de error general (fallback) */}
        {errors.submit && !alert?.isVisible && (
          <div className="mt-3 text-sm text-red-600">Registration Error: {escape(errors.submit)}</div>
        )}
      </form>

      {/* Diálogo para registro incompleto */}
      {showDialog && (
        <IncompleteRegistrationDialog
          type="carrier"
          onContinue={handleContinueWithPrevious}
          onDiscard={handleDiscardAndCreateNew}
          onClose={() => {}}
        />
      )}
    </div>
  );
};

export default CarrierRegistrationForm;